import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import './styles.css'

import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { Swiper as SwiperComponent, SwiperSlide } from 'swiper/react'
import { type Swiper } from 'swiper/types'

import clsx from 'clsx'
import { useAuth } from 'react-oidc-context'
import { IMainCarouselItem } from '../../api/personas'
import { useMediaQueryContext } from '../../contexts/MediaQueryContext'
import { useMainCarouselData } from '../../hooks/apiHooks/MentorHooks'
import { isAdmin } from '../../utils/isAdmin'
import { NextButton, PrevButton } from './NavButtons'
import { Slide } from './Slide'

interface SwiperSlideWithProgress extends HTMLElement {
  progress: number
}

const ParallaxCarousel: FC = () => {
  const { data, isLoading, isFetched } = useMainCarouselData()
  const swiperRef = useRef<Swiper | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { is2lg: isMobile } = useMediaQueryContext()
  const [carouselReady, setCarouselReady] = useState(false)
  const [screenWidth, setScreenWidth] = useState(() => window.innerWidth)

  const [showVideoIndex, setShowVideoIndex] = useState<number | null>(null)
  const [isHovered, setIsHovered] = useState(false)

  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const { user } = useAuth()

  // Memoize filtered slides to avoid re-computation on every render
  const filteredSlides = useMemo(() => {
    const filteredItems =
      data?.items?.filter((slide: IMainCarouselItem) => {
        if (slide.visibility === 'hidden') return false
        if (slide.visibility === 'admin' && !isAdmin(user)) return false
        if (slide.visibility === 'visitor' && user) return false
        return true
      }) || []

    if (filteredItems.length === 2) {
      const duplicate = filteredItems.map(el => ({
        ...el,
        id: `${el.id}-copy`,
      }))
      filteredItems.push(...duplicate)
    }

    return filteredItems
  }, [data?.items, user])

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowVideoIndex(0)
    }, 400)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.allowTouchMove = isMobile
    }
  }, [isMobile])

  // Memoize the resize handler to prevent unnecessary re-renders
  const handleResize = useCallback(() => {
    setScreenWidth(window.innerWidth)
  }, [])

  const handleSwiperInit = useCallback((swiper: Swiper) => {
    swiperRef.current = swiper
    setCarouselReady(true)

    swiper.on('transitionStart', () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    })

    swiper.on('transitionEnd', (s: Swiper) => {
      const { realIndex } = s
      timerRef.current = setTimeout(() => {
        setShowVideoIndex(realIndex)
      }, 400)
    })
  }, [])

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [handleResize])

  useEffect(() => {
    if (!containerRef.current || !swiperRef.current) return

    const resizeObserver = new ResizeObserver(() => {
      swiperRef.current?.update()
    })

    resizeObserver.observe(containerRef.current)

    return () => {
      resizeObserver.disconnect()
      if (swiperRef.current) {
        swiperRef.current.off('slideChange')
      }
    }
  }, [carouselReady])

  const handleProgress = useCallback(
    (swiper: Swiper) => {
      swiper.slides.forEach(slide => {
        const { progress } = slide as SwiperSlideWithProgress
        const background = slide.querySelector(
          '.tc-mv-slide-background',
        ) as HTMLElement
        // const slideContent = slide.querySelector(
        //   '.tc-mv-slide-content',
        // ) as HTMLElement
        // if (slideContent) {
        //   const opacity = 1 - Math.min(Math.abs(progress), 1)
        //   slideContent.style.opacity = `${opacity}`
        // }
        if (background) {
          background.style.transform = `translateX(${
            (progress * screenWidth) / 6
          }px)`
        }
      })
    },
    [screenWidth],
  )

  const handleSetTransition = useCallback(
    (swiper: Swiper, duration: number) => {
      swiper.slides.forEach((slide: HTMLElement) => {
        const bg = slide.querySelector('.tc-mv-slide-background') as HTMLElement
        if (bg) {
          bg.style.transition = `transform ${duration}ms`
        }
        // const slideContent = slide.querySelector(
        //   '.tc-mv-slide-content',
        // ) as HTMLElement
        // if (slideContent) {
        //   slideContent.style.transition = `opacity ${duration}ms`
        // }
      })
    },
    [],
  )

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
  }, [])

  if (isLoading) {
    return (
      <div className="tc-mv-main-carousel-wrapper">
        <div className="h-full w-full tc-mv-blur rounded-regular border-none animate-pulse" />
      </div>
    )
  }

  if (isFetched && filteredSlides.length === 0) {
    return null
  }

  return (
    <div
      ref={containerRef}
      className={clsx('tc-mv-main-carousel-wrapper', { '!pr-0': isMobile })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <SwiperComponent
        onProgress={handleProgress}
        onSetTransition={handleSetTransition}
        watchSlidesProgress
        slidesPerView="auto"
        loop={filteredSlides.length > 1}
        speed={isMobile ? 800 : 1600}
        pagination={{
          clickable: true,
          enabled: filteredSlides.length > 1,
        }}
        modules={[Pagination, Navigation, Autoplay]}
        className="w-full h-full tc-mv-main-carousel"
        autoplay={
          filteredSlides.length > 1
            ? {
                delay: 6000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }
            : false
        }
        spaceBetween={16}
        allowTouchMove={isMobile}
        onSwiper={handleSwiperInit}
      >
        {filteredSlides.map((slide: IMainCarouselItem, index: number) => (
          <SwiperSlide
            key={slide.id}
            virtualIndex={index}
            className={clsx({
              '!w-full': filteredSlides.length === 1,
            })}
          >
            <Slide slide={slide} withVideo={showVideoIndex === index} />
          </SwiperSlide>
        ))}
        {filteredSlides.length > 1 && (
          <div
            className={clsx(
              'transition-opacity duration-300 ease-in-out',
              isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )}
          >
            <NextButton />
            <PrevButton />
          </div>
        )}
      </SwiperComponent>
    </div>
  )
}

export { ParallaxCarousel }
